import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";

// Third party
import * as Yup from "yup";

// Material
import { AccordionDetails, Box, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";

// Styling
import flexStyles from "../../../../style/flex";

// Utils
import useValidator, { createFakeEvent } from "../../../../utils/useValidator";

// Components
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import { KeyboardDatePicker } from "@material-ui/pickers";
import moment from "moment";
import {
  StyledAccordion,
  StyledAccordionSummary,
} from "../../../../Components/UI/Accordion";
import PrimaryButton from "../../../../Components/UI/Buttons/PrimaryButton";
import SecondaryButton from "../../../../Components/UI/Buttons/SecondaryButton";
import FormLayout from "../../../../Components/UI/FormLayout";
import SkeletonLoader from "../../../../Components/UI/SkeletonLoader";
import { refererList, referrerTypes } from "../../../../Data/enquiry";
import {
  addWebEnquiry,
  editWebEnquiry,
  getViewData,
} from "../../../../Stores/WebsiteEnquiries/actions";
import accordionStyles from "../../../../style/accordion";
import {
  CareFor,
  CareType,
  FormType,
  SourceList,
  TitleList,
} from "../../../../utils/enquiriesData";

// Actions

const WebsiteEnquiresAdd = () => {
  const dispatch = useDispatch();

  const accordClasses = accordionStyles();
  const { webEnqID } = useParams();
  const history = useHistory();
  const isEditing = useRouteMatch(
    `/d/location/edit/:id/website-enquiries/edit/:webEnqID`,
  )?.isExact;

  const flexClasses = flexStyles();

  const { activeLocation } = useSelector(
    (state) => state.SlickCareProfilesReducer,
  );
  const { isFetching, currentWebEnq, isAdding, isFetchingData, isUpdating } =
    useSelector((state) => state.WebsiteEnquiriesReducer);

  let loading = isFetching || isUpdating || isFetchingData || isAdding;

  function onSubmit() {
    let data = {
      ...values,
      location_id: activeLocation?.id,
    };

    if (data?.type === "Tour") {
      data.preferred_time_for_tour = `${data.preferred_time_for_tour}:00`;
    }
    if (isEditing) {
      dispatch(editWebEnquiry({ data, id: currentWebEnq?.id }));
    } else {
      dispatch(addWebEnquiry({ data, history }));
    }
  }

  const {
    errors,
    touched,
    values,
    handleSubmit,
    handleChange,
    setValues,
    clearFormState,
    getFieldProps,
  } = useValidator({
    initialValues: {
      location_id: "",
      assigned_user: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      message: "",
      type: "",
      stage: "Prospecting",
      whos_need_care: "",
      soon_care_need: "",
      type_of_funding: "",
      type_of_care: "",
      length_of_stay: "",
      date_of_birth: "",
      medical_condition: "",
      name_of_social_worker: "",
      title: "",
      tour_type: "",
      preferred_date_for_tour: "",
      preferred_time_for_tour: "",
      source: "",
      referrer_type: "",
      referrer: "",
    },
    onSubmit,
    validationSchema: Yup.object({
      first_name: Yup.string().required("First name is Required!"),
      last_name: Yup.string().required("Last name is Required!"),
      title: Yup.string().required("Title is Required!"),
      type: Yup.string().required("Enquiry for is Required!"),
      source: Yup.string().required("Soruce is Required!"),
      referrer: Yup.string()
        .nullable()
        .when("referrer_type", {
          is: (referrer_type) => referrer_type?.length > 0,
          then: Yup.string().required("This field is required!"),
        }),
      referrer_type: Yup.string().required(`Referrer type is required!`),
      phone: Yup.string()
        .test(
          `Contact Number format`,
          `Invalid Contact Number format!`,
          function (value) {
            const mobileRegex =
              /^((\+44(\s\(0\)\s|\s0\s|\s)?)|0)?\d{4}(\s)?\d{6}$/;
            return (
              typeof value === "string" &&
              mobileRegex.test(value.replace(/\s/g, ""))
            );
          },
        )
        .required("Phone Number is Required"),
      email: Yup.string()
        .email("Invalid Email!")
        .required("Email is required!"),
      tour_type: Yup.string()
        .nullable()
        .when("type", {
          is: (type) => type === "Tour",
          then: Yup.string().required("This field is required!"),
        }),
      preferred_date_for_tour: Yup.string()
        .nullable()
        .when("type", {
          is: (type) => type === "Tour",
          then: Yup.string().required("This field is required!"),
        }),
      preferred_time_for_tour: Yup.string()
        .nullable()
        .when("type", {
          is: (type) => type === "Tour",
          then: Yup.string().required("This field is required!"),
        }),
    }),
  });

  useEffect(() => {
    clearFormState();
  }, []);

  useEffect(() => {
    if (webEnqID && isEditing) {
      dispatch(getViewData(webEnqID));
    }
  }, [webEnqID, isEditing]);

  useEffect(() => {
    if (currentWebEnq?.id && isEditing) {
      let time = currentWebEnq?.preferred_time_for_tour;
      setValues({
        ...values,
        ...currentWebEnq,
        preferred_time_for_tour: time
          ? moment(time, "HH:mm:ss").format("HH:mm")
          : "",
      });
    }
  }, [currentWebEnq, isEditing]);

  return (
    <>
      <FormLayout
        title={"Website Enquiry"}
        mode={isEditing ? "Edit" : "Add"}
        pageName={"Website Enquiry"}
        parentLink={`/d/location/edit/${activeLocation?.id}/website-enquiries/listing`}
      >
        <form onSubmit={handleSubmit}>
          <Box className={flexClasses.flexContainer}>
            <Box className={flexClasses.item}>
              <StyledAccordion defaultExpanded={true}>
                <StyledAccordionSummary
                  classes={{
                    content: accordClasses.content,
                    expanded: accordClasses.expanded,
                  }}
                >
                  <Box className={accordClasses.accordHead}>
                    <ArrowRightIcon />
                    <h3>Care Seeker Details</h3>
                  </Box>
                </StyledAccordionSummary>
                <AccordionDetails style={{ flexDirection: "column" }}>
                  <Box className={flexClasses.flexContainer}>
                    <Box className={flexClasses.itemMD} id="first_name">
                      {loading ? (
                        <SkeletonLoader type="text" height={40} />
                      ) : (
                        <Autocomplete
                          options={TitleList}
                          getOptionLabel={(option) => option}
                          value={values?.title}
                          onChange={(event, newValue) => {
                            handleChange("title")(
                              createFakeEvent(newValue ?? ""),
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Title"
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                              size="small"
                              error={!!(touched.title && errors.title)}
                              helperText={touched.title && errors.title}
                            />
                          )}
                        />
                      )}
                    </Box>

                    <Box className={flexClasses.itemMD} id="first_name">
                      {loading ? (
                        <SkeletonLoader type="text" height={40} />
                      ) : (
                        <TextField
                          variant="outlined"
                          size="small"
                          label="First Name"
                          InputLabelProps={{ shrink: true }}
                          value={values.first_name}
                          helperText={touched.first_name && errors.first_name}
                          error={!!(touched.first_name && errors.first_name)}
                          {...getFieldProps("first_name")}
                          className={flexClasses.input}
                        />
                      )}
                    </Box>
                    <Box className={flexClasses.itemMD} id="last_name">
                      {loading ? (
                        <SkeletonLoader type="text" height={40} />
                      ) : (
                        <TextField
                          variant="outlined"
                          size="small"
                          label="Last Name"
                          InputLabelProps={{ shrink: true }}
                          value={values.last_name}
                          helperText={touched.last_name && errors.last_name}
                          error={!!(touched.last_name && errors.last_name)}
                          {...getFieldProps("last_name")}
                          className={flexClasses.input}
                        />
                      )}
                    </Box>
                    <Box className={flexClasses.itemMD} id="email">
                      {loading ? (
                        <SkeletonLoader type="text" height={40} />
                      ) : (
                        <TextField
                          variant="outlined"
                          size="small"
                          label="Email"
                          InputLabelProps={{ shrink: true }}
                          value={values.email}
                          helperText={touched.email && errors.email}
                          error={!!(touched.email && errors.email)}
                          {...getFieldProps("email")}
                          className={flexClasses.input}
                        />
                      )}
                    </Box>
                    <Box className={flexClasses.itemMD} id="phone">
                      {loading ? (
                        <SkeletonLoader type="text" height={40} />
                      ) : (
                        <TextField
                          type="number"
                          variant="outlined"
                          size="small"
                          label="Phone"
                          InputLabelProps={{ shrink: true }}
                          value={values.phone}
                          helperText={touched.phone && errors.phone}
                          error={!!(touched.phone && errors.phone)}
                          {...getFieldProps("phone")}
                          className={flexClasses.input}
                        />
                      )}
                    </Box>
                  </Box>
                </AccordionDetails>
              </StyledAccordion>
            </Box>
            <Box className={flexClasses.item}>
              <StyledAccordion defaultExpanded={true}>
                <StyledAccordionSummary
                  classes={{
                    content: accordClasses.content,
                    expanded: accordClasses.expanded,
                  }}
                >
                  <Box className={accordClasses.accordHead}>
                    <ArrowRightIcon />
                    <h3>Care Details</h3>
                  </Box>
                </StyledAccordionSummary>
                <AccordionDetails style={{ flexDirection: "column" }}>
                  <Box className={flexClasses.flexContainer}>
                    <Box className={flexClasses.itemMD} id="type">
                      {loading ? (
                        <SkeletonLoader type="text" height={40} />
                      ) : (
                        <Autocomplete
                          options={FormType}
                          getOptionLabel={(option) => option}
                          value={values?.type}
                          onChange={(event, newValue) => {
                            handleChange("type")(
                              createFakeEvent(newValue ?? ""),
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              label="Enquiry for"
                              InputLabelProps={{ shrink: true }}
                              error={!!(touched.type && errors.type)}
                              helperText={touched.type && errors.type}
                            />
                          )}
                        />
                      )}
                    </Box>
                    <Box className={flexClasses.itemMD} id="whos_need_care">
                      {loading ? (
                        <SkeletonLoader type="text" height={40} />
                      ) : (
                        <Autocomplete
                          options={CareFor}
                          getOptionLabel={(option) => option}
                          value={values?.whos_need_care}
                          onChange={(event, newValue) => {
                            handleChange("whos_need_care")(
                              createFakeEvent(newValue ?? ""),
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              label="Who needs care"
                              InputLabelProps={{ shrink: true }}
                              error={
                                !!(
                                  touched.whos_need_care &&
                                  errors.whos_need_care
                                )
                              }
                              helperText={
                                touched.whos_need_care && errors.whos_need_care
                              }
                            />
                          )}
                        />
                      )}
                    </Box>
                    <Box className={flexClasses.itemMD} id="type_of_care">
                      {loading ? (
                        <SkeletonLoader type="text" height={40} />
                      ) : (
                        <Autocomplete
                          options={CareType}
                          getOptionLabel={(option) => option}
                          value={values?.type_of_care}
                          onChange={(event, newValue) => {
                            handleChange("type_of_care")(
                              createFakeEvent(newValue ?? ""),
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Type of care?"
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                              size="small"
                              error={
                                !!(touched.type_of_care && errors.type_of_care)
                              }
                              helperText={
                                touched.type_of_care && errors.type_of_care
                              }
                            />
                          )}
                        />
                      )}
                    </Box>
                    {/* <Box className={flexClasses.itemMD} id="soon_care_need">
                      {loading ? (
                        <SkeletonLoader type="text" height={40} />
                      ) : (
                        <Autocomplete
                          options={CareRequired}
                          getOptionLabel={(option) => option}
                          value={values?.soon_care_need}
                          onChange={(event, newValue) => {
                            handleChange("soon_care_need")(
                              createFakeEvent(newValue ?? "")
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="How soon care is needed?"
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                              size="small"
                              error={
                                !!(
                                  touched.soon_care_need &&
                                  errors.soon_care_need
                                )
                              }
                              helperText={
                                touched.soon_care_need && errors.soon_care_need
                              }
                            />
                          )}
                        />
                      )}
                    </Box> */}
                    {/* <Box className={flexClasses.itemMD} id="type_of_funding">
                      {loading ? (
                        <SkeletonLoader type="text" height={40} />
                      ) : (
                        <Autocomplete
                          options={CareRequired}
                          getOptionLabel={(option) => option}
                          value={values?.type_of_funding}
                          onChange={(event, newValue) => {
                            handleChange("type_of_funding")(
                              createFakeEvent(newValue ?? "")
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Type of funding?"
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                              size="small"
                              error={
                                !!(
                                  touched.type_of_funding &&
                                  errors.type_of_funding
                                )
                              }
                              helperText={
                                touched.type_of_funding &&
                                errors.type_of_funding
                              }
                            />
                          )}
                        />
                      )}
                    </Box> */}
                  </Box>
                </AccordionDetails>
              </StyledAccordion>
            </Box>
            {values.type === "Tour" && (
              <Box className={flexClasses.item}>
                <StyledAccordion defaultExpanded={true}>
                  <StyledAccordionSummary
                    classes={{
                      content: accordClasses.content,
                      expanded: accordClasses.expanded,
                    }}
                  >
                    <Box className={accordClasses.accordHead}>
                      <ArrowRightIcon />
                      <h3>Tour Details</h3>
                    </Box>
                  </StyledAccordionSummary>
                  <AccordionDetails style={{ flexDirection: "column" }}>
                    <Box className={flexClasses.flexContainer}>
                      <Box className={flexClasses.itemSMD}>
                        {loading ? (
                          <SkeletonLoader type="text" height={40} />
                        ) : (
                          <Autocomplete
                            options={["Onsite", "Remote", "Both"]}
                            getOptionLabel={(option) => option}
                            value={values?.tour_type}
                            onChange={(event, newValue) => {
                              handleChange("tour_type")(
                                createFakeEvent(newValue ?? ""),
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                label="Tour type"
                                InputLabelProps={{ shrink: true }}
                                error={
                                  !!(touched.tour_type && errors.tour_type)
                                }
                                helperText={
                                  touched.tour_type && errors.tour_type
                                }
                              />
                            )}
                          />
                        )}
                      </Box>
                      <Box className={flexClasses.itemSMD}>
                        {loading ? (
                          <SkeletonLoader type="text" height={40} />
                        ) : (
                          <KeyboardDatePicker
                            autoOk
                            disablePast
                            variant="inline"
                            inputVariant="outlined"
                            format="yyyy-MM-dd"
                            size="small"
                            clearable
                            value={
                              values?.preferred_date_for_tour !== ""
                                ? values?.preferred_date_for_tour
                                : null
                            }
                            label="Preferred Date"
                            InputLabelProps={{ shrink: true }}
                            onChange={(date) => {
                              setValues({
                                ...values,
                                preferred_date_for_tour:
                                  moment(date).format("YYYY-MM-DD"),
                              });
                            }}
                            style={{ width: "100%" }}
                            KeyboardButtonProps={{
                              "aria-label": "change date",
                            }}
                            error={
                              !!(
                                touched.preferred_date_for_tour &&
                                errors.preferred_date_for_tour
                              )
                            }
                            helperText={
                              touched.preferred_date_for_tour &&
                              errors.preferred_date_for_tour
                            }
                          />
                        )}
                      </Box>
                      <Box className={flexClasses.itemSMD}>
                        {loading ? (
                          <SkeletonLoader type="text" height={40} />
                        ) : (
                          <TextField
                            size="small"
                            variant="outlined"
                            id="time"
                            label="Preferred Time"
                            type="time"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            style={{ width: "100%" }}
                            value={values.preferred_time_for_tour}
                            onChange={(e) => {
                              setValues({
                                ...values,
                                preferred_time_for_tour: e?.target?.value,
                              });
                            }}
                            helperText={
                              touched.preferred_time_for_tour &&
                              errors.preferred_time_for_tour
                            }
                            error={
                              !!(
                                touched.preferred_time_for_tour &&
                                errors.preferred_time_for_tour
                              )
                            }
                          />
                        )}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </StyledAccordion>
              </Box>
            )}
            <Box className={flexClasses.item}>
              <StyledAccordion defaultExpanded={true}>
                <StyledAccordionSummary
                  classes={{
                    content: accordClasses.content,
                    expanded: accordClasses.expanded,
                  }}
                >
                  <Box className={accordClasses.accordHead}>
                    <ArrowRightIcon />
                    <h3>Source Details</h3>
                  </Box>
                </StyledAccordionSummary>
                <AccordionDetails style={{ flexDirection: "column" }}>
                  <Box className={flexClasses.flexContainer}>
                    <Box className={flexClasses.itemSMD}>
                      {loading ? (
                        <SkeletonLoader type="text" height={40} />
                      ) : (
                        <Autocomplete
                          options={SourceList}
                          getOptionLabel={(option) => option}
                          value={values?.source}
                          onChange={(event, newValue) => {
                            handleChange("source")(
                              createFakeEvent(newValue ?? ""),
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              label="Source"
                              error={!!(touched.source && errors.source)}
                              helperText={touched.source && errors.source}
                            />
                          )}
                        />
                      )}
                    </Box>
                    <Box className={flexClasses.itemSMD}>
                      {loading ? (
                        <SkeletonLoader type="text" height={40} />
                      ) : (
                        <Autocomplete
                          options={referrerTypes}
                          getOptionLabel={(option) => option}
                          value={values?.referrer_type}
                          onChange={(event, newValue) => {
                            handleChange("referrer_type")(
                              createFakeEvent(newValue ?? ""),
                            );
                            handleChange("referer")(createFakeEvent(""));
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Referrer Type"
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                              size="small"
                              error={
                                !!(
                                  touched.referrer_type && errors.referrer_type
                                )
                              }
                              helperText={
                                touched.referrer_type && errors.referrer_type
                              }
                            />
                          )}
                        />
                      )}
                    </Box>
                    {values?.referrer_type && (
                      <Box className={flexClasses.itemSMD} id="referrer_type">
                        {loading ? (
                          <SkeletonLoader type="text" height={40} />
                        ) : (
                          <Autocomplete
                            options={
                              refererList[
                                values?.referrer_type
                                  ?.replaceAll(" ", "")
                                  .replaceAll("-", "")
                              ] ?? []
                            }
                            getOptionLabel={(option) => option}
                            value={values?.referrer}
                            onChange={(event, newValue) => {
                              handleChange("referrer")(
                                createFakeEvent(newValue ?? ""),
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Referrer"
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                size="small"
                                error={!!(touched.referrer && errors.referrer)}
                                helperText={touched.referrer && errors.referrer}
                              />
                            )}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </StyledAccordion>
            </Box>
          </Box>
          <div
            className={`${flexClasses.actionWrap} ${flexClasses.marginNone}`}
          >
            <div className={`${flexClasses.actions} `}>
              <SecondaryButton onClick={() => history.goBack()}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" hideIcon={true} disabled={loading}>
                {isEditing ? "Update" : "Add"}
              </PrimaryButton>
            </div>
          </div>
        </form>
      </FormLayout>
    </>
  );
};

export default WebsiteEnquiresAdd;
